import React, { useEffect, useRef, useState } from 'react';

/**
 * Install instructions for browsers that cannot install the app themselves.
 *
 * The header's install control used to render nothing outside Chromium, because
 * both signals it hung off (`beforeinstallprompt`, `getInstalledRelatedApps`)
 * are Chromium-only. Safari users saw no button at all and had no way to learn
 * that "Ablage > Zum Dock hinzufuegen" is their path, so they never got the
 * badge either. This dialog is what that button opens instead.
 *
 * Three modes, all carrying the same shape so there is one dialog, not three:
 *   safari      - Ablage > Zum Dock hinzufuegen (macOS), with screenshot
 *   chromium    - installed already, or the browser menu's install entry
 *   unsupported - Firefox and friends: switch browsers
 *
 * Notifications get their own row in every mode: Safari only paints the dock
 * badge once notification permission is granted INSIDE the installed web app,
 * and Safari only shows that permission prompt on a real user gesture, which
 * the automatic request in useTaskAlerts is not.
 */

const SAFARI_SHOT = '/help/safari-ablage-menu.svg';

const CONTENT = {
  safari: {
    title: 'Als App installieren',
    steps: [
      'Menü „Ablage“ öffnen',
      '„Zum Dock hinzufügen …“ wählen',
      'DeskBot ab jetzt aus dem Dock starten',
    ],
    figure: { src: SAFARI_SHOT, alt: 'Safari-Menü „Ablage“ mit „Zum Dock hinzufügen …“' },
  },
  chromium: {
    title: 'Als App installieren',
    steps: [
      'Browser-Menü oben rechts öffnen',
      '„DeskBot installieren“ wählen',
    ],
    figure: null,
  },
  unsupported: {
    title: 'In diesem Browser nicht möglich',
    steps: [
      'Bitte DeskBot in Chrome, Edge oder Safari öffnen',
    ],
    figure: null,
  },
};

const NOTIFICATION_TEXT = {
  granted: 'Benachrichtigungen aktiv',
  denied: 'Benachrichtigungen blockiert',
  default: 'Benachrichtigungen für neue Aufgaben',
};

const InstallHelpModal = ({ mode, onClose }) => {
  const dialogRef = useRef(null);
  // Permission is not reactive, so a click has to re-read it. Kept in state only
  // to repaint this row after the browser's prompt resolves.
  const [permission, setPermission] = useState(
    () => ('Notification' in window ? Notification.permission : null)
  );

  // Focus the dialog itself, not the ×: the close button would show a focus ring
  // and read as pre-selected. Kept apart from the key handler because `onClose`
  // is a fresh arrow on every Layout render, and Layout re-renders on every
  // call-monitor message: a combined effect would yank focus back through a call.
  useEffect(() => { dialogRef.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Safari grants this only from a user gesture, which is the whole point of
  // asking here rather than on mount.
  const askForNotifications = () => {
    if (!('Notification' in window)) return;
    try {
      const result = Notification.requestPermission(setPermission);
      if (result?.then) result.then(setPermission).catch(() => {});
    } catch {
      /* older Safari signatures throw rather than reject */
    }
  };

  const content = CONTENT[mode] || CONTENT.unsupported;

  return (
    <div
      className="shortcut-help-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="shortcut-help-modal install-help-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-help-title"
      >
        <div className="shortcut-help-header">
          <h2 id="install-help-title" className="install-help-title">{content.title}</h2>
          <button
            type="button"
            className="shortcut-help-close"
            onClick={onClose}
            aria-label="Schließen"
          >
            ×
          </button>
        </div>

        <div className="shortcut-help-body install-help-body">
          {/* A single instruction is a sentence, not a numbered list. */}
          {content.steps.length === 1 ? (
            <p className="install-help-line">{content.steps[0]}</p>
          ) : (
            <ol className="install-help-steps">
              {content.steps.map((step, i) => (
                <li key={i}>
                  <span className="install-help-step-num">{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          )}

          {content.figure && (
            <img
              className="install-help-figure"
              src={content.figure.src}
              alt={content.figure.alt}
              // The steps above say the same thing in words, so a missing asset
              // costs nothing but the picture.
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}

          {permission && (
            <div className="install-help-notify">
              <span>{NOTIFICATION_TEXT[permission] || NOTIFICATION_TEXT.default}</span>
              {permission === 'default' && (
                <button type="button" className="button install-help-notify-btn" onClick={askForNotifications}>
                  Aktivieren
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallHelpModal;
